/**
 * /outbox — Outbox offline view (FWA-PWA-029).
 *
 * Muestra items pendientes/failed/conflicts + permite sync manual + descartar.
 * PII tier Crítico: NO renderiza payload PII directamente (solo endpoint + status).
 */
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { useOnlineStatus } from '../../lib/hooks/useOnlineStatus.js';
import {
  useDeleteOutboxItem,
  useOutboxCounts,
  useOutboxList,
  useSyncOutbox,
} from '../../lib/hooks/useOutbox.js';
import type { OutboxStatus } from '../../lib/outbox/types.js';

const STATUS_LABEL: Record<OutboxStatus, string> = {
  pending: 'En cola',
  syncing: 'Sincronizando',
  success: 'OK',
  failed: 'Falló',
};

export function OutboxView() {
  const online = useOnlineStatus();
  const list = useOutboxList();
  const counts = useOutboxCounts();
  const sync = useSyncOutbox();
  const del = useDeleteOutboxItem();

  const items = list.data ?? [];
  const c = counts.data ?? { pending: 0, syncing: 0, success: 0, failed: 0 };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Outbox offline</h1>
        <div className="flex items-center gap-3">
          <span
            className={`text-sm font-medium ${online ? 'text-green-700' : 'text-red-700'}`}
            aria-label={online ? 'En línea' : 'Sin conexión'}
          >
            {online ? '🟢 En línea' : '🔴 Sin conexión'}
          </span>
          <Button
            variant="primary"
            onClick={() => sync.mutate()}
            disabled={!online || sync.isPending || items.length === 0}
          >
            {sync.isPending ? 'Sincronizando…' : 'Sync ahora'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <CountTile label="Pendientes" value={c.pending} tone="blue" />
        <CountTile label="Sincronizando" value={c.syncing} tone="amber" />
        <CountTile label="Fallidos" value={c.failed} tone="red" />
        <CountTile label="OK" value={c.success} tone="green" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Items en cola</CardTitle>
        </CardHeader>
        <CardBody>
          {items.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              Sin items pendientes. Todo sincronizado.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr className="text-left">
                  <th className="py-2 font-medium text-gray-700">Endpoint</th>
                  <th className="py-2 font-medium text-gray-700">Método</th>
                  <th className="py-2 font-medium text-gray-700">Estado</th>
                  <th className="py-2 font-medium text-gray-700">Intentos</th>
                  <th className="py-2 font-medium text-gray-700">Creado</th>
                  <th className="py-2 font-medium text-gray-700" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 font-mono text-xs">{item.endpoint}</td>
                    <td className="py-2">{item.method}</td>
                    <td className="py-2">
                      <StatusBadge status={item.status} conflict={item.conflict} />
                    </td>
                    <td className="py-2">{item.attempts}</td>
                    <td className="py-2 text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-800 text-xs"
                        onClick={() => del.mutate(item.id)}
                        disabled={del.isPending}
                      >
                        Descartar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function CountTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'blue' | 'amber' | 'red' | 'green';
}) {
  const toneClass = {
    blue: 'bg-blue-50 text-blue-800',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-800',
    green: 'bg-green-50 text-green-800',
  }[tone];
  return (
    <div className={`p-3 rounded-md ${toneClass}`}>
      <div className="text-xs uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function StatusBadge({ status, conflict }: { status: OutboxStatus; conflict: boolean }) {
  const tone =
    status === 'success'
      ? 'bg-green-100 text-green-800'
      : status === 'failed'
        ? 'bg-red-100 text-red-800'
        : status === 'syncing'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-blue-100 text-blue-800';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs ${tone}`}>
      {STATUS_LABEL[status]}
      {conflict ? ' (conflicto 412)' : ''}
    </span>
  );
}
